/**
 * Keeps the catalog holding the Cloud Workspaces this device uses, for the whole session.
 *
 * The Runtime Web Client is a static page; the only durable record of what it uses is the list of
 * workspace ids in Client Settings. Registrations are shared here, by the app-level
 * `CloudWorkspaceConnections` and the Connections page, so a workspace is registered once however
 * many readers of the account's listing there are.
 */
import { type CloudWorkspace, EnvironmentId } from "@afar-research/contracts";
import { Atom } from "effect/unstable/reactivity";

import { readLocalApi } from "../localApi";
import { appAtomRegistry } from "../rpc/atomRegistry";

import { planCloudWorkspaceRegistration } from "./cloudWorkspaceRegistration.logic";
import { seedConnectEnvironments } from "./connectEnvironments";
import {
  connectSavedEnvironment,
  disconnectConnectEnvironments,
  removeSavedEnvironment,
} from "./platform";

/**
 * Whether this session has settled which Cloud Workspaces it uses: an answered listing has been
 * applied, or there is no signed-in account to list. Until then an empty catalog means "not found
 * yet", not "none". It never unsettles: once the shell has opened, a later account change is
 * answered by the Environments it registers, not by holding the shell back again.
 */
export const cloudWorkspacesSettledAtom = Atom.make(false).pipe(
  Atom.keepAlive,
  Atom.withLabel("cloud-workspaces-settled"),
);
export const cloudWorkspaceIdsAtom = Atom.make<{ readonly ids: ReadonlySet<string> }>({
  ids: new Set<string>(),
}).pipe(Atom.keepAlive);
/**
 * The workspace this browser last used, remembered across reloads so reopening the app returns to
 * it. A convenience only: a remembered workspace the account no longer lists selects nothing.
 */
const SESSION_SELECTION_KEY = "afar-research:cloud-workspace-selection";

function readRememberedSelection(): string | null {
  try {
    return window.localStorage.getItem(SESSION_SELECTION_KEY);
  } catch {
    return null;
  }
}

export const sessionCloudWorkspaceIdAtom = Atom.make<string | null>(readRememberedSelection()).pipe(
  Atom.keepAlive,
);

export function selectCloudWorkspaceForSession(workspaceId: string | null) {
  appAtomRegistry.set(sessionCloudWorkspaceIdAtom, workspaceId);
  try {
    if (workspaceId === null) window.localStorage.removeItem(SESSION_SELECTION_KEY);
    else window.localStorage.setItem(SESSION_SELECTION_KEY, workspaceId);
  } catch {
    // Storage is unavailable here; the selection still holds for this page.
  }
}

export function settleCloudWorkspaces() {
  appAtomRegistry.set(cloudWorkspacesSettledAtom, true);
}

/** Each registered workspace's label, so an unchanged one is left alone. */
const registered = new Map<string, string>();
/** Saved workspaces this client let go of, so each departure is asked for once (#940). */
const released = new Set<string>();

function registerCloudWorkspace(workspace: Pick<CloudWorkspace, "id" | "label">, connect = false) {
  connectSavedEnvironment(
    {
      environmentId: EnvironmentId.make(workspace.id),
      environmentLabel: workspace.label,
      // A connection always uses the gateway route Connect names when it brokers the
      // introduction (ADR-0150), so this is only a stable label.
      endpoint: `https://${workspace.id}.cloud-workspace.invalid`,
      applicationRelease: "connect",
      scopes: ["environment:read", "operations:read", "operations:write"],
      sessionExpiresAt: 0,
    },
    { connect, replace: true },
  );
}

/**
 * Applies one answered listing. Only an answered listing may drive this: an unanswered one has not
 * learned that a workspace is gone, and treating it as empty would forget reachable workspaces.
 */
export function reconcileCloudWorkspaces(input: {
  readonly accountKey: string;
  readonly listed: ReadonlyArray<CloudWorkspace>;
  readonly added: ReadonlyArray<string> | undefined;
  readonly catalog: ReadonlyMap<string, { readonly local: boolean }>;
}) {
  // Machines this device reached through an AFAR Network are kept the same way as the workspaces
  // it added; without them here, every listing would let go of them (ADR-0146).
  const remembered = readLocalApi()?.connect?.networks?.rememberedMachines() ?? [];
  const plan = planCloudWorkspaceRegistration({
    ...input,
    remembered,
    registered,
    released,
  });
  if (plan.seed !== null)
    void seedConnectEnvironments(
      input.accountKey,
      plan.seed.map((id) => EnvironmentId.make(id)),
    );
  for (const workspace of plan.register) {
    registered.set(workspace.id, workspace.label);
    released.delete(workspace.id);
    registerCloudWorkspace(workspace);
  }
  for (const environmentId of plan.release) forgetCloudWorkspace(environmentId);
}

/**
 * Connects a workspace the user just started. A connection refused because the user had stopped it
 * stays blocked until it is asked again, so Start asks.
 */
export function reconnectCloudWorkspace(workspace: Pick<CloudWorkspace, "id" | "label">) {
  selectCloudWorkspaceForSession(workspace.id);
  registered.set(workspace.id, workspace.label);
  released.delete(workspace.id);
  registerCloudWorkspace(workspace, true);
}

/** Lets go of one saved workspace; adding it back registers it again. */
export function forgetCloudWorkspace(environmentId: string) {
  if (appAtomRegistry.get(sessionCloudWorkspaceIdAtom) === environmentId)
    selectCloudWorkspaceForSession(null);
  released.add(environmentId);
  registered.delete(environmentId);
  removeSavedEnvironment(EnvironmentId.make(environmentId));
}

/** Returns the module to its first-load state, so one test's registrations never reach the next. */
export function __resetCloudWorkspaceRegistrationsForTests() {
  registered.clear();
  released.clear();
}

/** Signing out or switching accounts drops every Cloud Workspace the old account held. */
export function disconnectCloudWorkspaces() {
  selectCloudWorkspaceForSession(null);
  disconnectConnectEnvironments();
  registered.clear();
  released.clear();
}
